// TASK-603: the aggregated alert card — count, named clients, CHF at stake, one
// plain sentence, and actions that navigate. Labels with no value never render.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronDown, ChevronUp, X } from 'lucide-react';
import type { AlertGroup } from '../../../utils/groupAlerts';
import { clientEvidenceHref } from '../signalSubject';
import type { AlertLevel, DashboardAlert } from '../../../types/dashboard';
import AlertRow, { SNOOZE_OPTIONS, addDays } from './AlertRow';
import { policyPermits } from '../../../utils/signalPolicy';
import { useViewerRole } from '../../../contexts/AccountContext';

/** Most severe first, so the distribution badge reads the same way every render. */
const LEVEL_ORDER: AlertLevel[] = ['critical', 'high', 'medium', 'info'];

const LEVEL_STYLES: Record<string, { border: string; badge: string }> = {
  critical: { border: 'border-red-300', badge: 'bg-red-100 text-red-800' },
  high: { border: 'border-orange-300', badge: 'bg-orange-100 text-orange-800' },
  medium: { border: 'border-yellow-300', badge: 'bg-yellow-100 text-yellow-800' },
  info: { border: 'border-blue-200', badge: 'bg-blue-100 text-blue-800' },
};

// Per-cause primary action: label + where it navigates (existing routes only).
// TASK-756: the label is keyed on the CAUSE (`alerts.action.{causeKey}`) with the English
// below as its default value, so an unfilled locale renders a real button label rather than
// a key. The route is code and is never translated.
// TASK-820: `CAUSE_ACTIONS` is GONE, and with it `DNA_TAB_CAUSES` — the stopgap TASK-819
// added to stop meeting reminders being sent to the DNA tab.
//
// Four hand-written entries decided both the LABEL and the DESTINATION for a server catalogue
// of twelve, so a group the table did not know rendered no primary action at all. The starkest
// case: `reprofile_due`, whose policy allows `investigate / assign / snooze / resolve`, showed
// ZERO buttons — no `CAUSE_ACTIONS` entry for the action, and `dismiss` is correctly absent
// from its policy so the dismiss branch suppressed itself too. The one group with the richest
// server-side action contract was the one with nothing to click.
//
// Actions now come from `action_policy.allowed_dispositions`, and the destination from
// `DESTINATIONS` below — which is about NAVIGATION (which surface holds this evidence), not
// about which actions are legal. That distinction is why the map survives at all: the policy
// catalogue deliberately owns legality and says nothing about routes.
// ponytail: exported from a component file, which trips react-refresh/only-export-components.
// It belongs beside `clientEvidenceHref` in `signalSubject.ts` — the code there already says
// the deep-link map is "a property of the surface that WRITES the link", and since TASK-923
// there are TWO such surfaces (this card and AlertsPage's title click). Left here because
// moving it drags `clientPath` and `driftHref` with it; move when a third caller appears.
export const DESTINATIONS: Record<string, (g: AlertGroup) => string> = {
  // A conflict's evidence is the personalization tab, focused on the instrument (TASK-830).
  dna_fit_conflict: (g) => clientPath(g, 'personalization'),
  // The house view, which TASK-831 built (TASK-829 made the tab routable).
  cio_stale_sell: (g) => clientPath(g, 'cio'),
  // The drift view, focused on the breaching sub-asset class (TASK-832).
  portfolio_drift_breach: (g) => driftHref(g),
  portfolio_drift_rebalance: (g) => driftHref(g),
  // TASK-828 owed this ticket a destination for the mandatory re-profile obligation. The
  // risk-profile form IS the re-profiling flow — the alert's own `action_required` says
  // "Re-profile the client", and it had nowhere to point.
  reprofile_due: (g) => clientPath(g, 'risk-profile'),
  kyc_expired: (g) => clientPath(g, 'kyc'),
  kyc_staged_pending: (g) => clientPath(g, 'kyc'),
  // TASK-923: the screening EVIDENCE — the match, its categories, its provenance — lives on
  // the compliance tab's screening sub-tab. This pointed at the KYC tab, which holds the
  // profile and not one word about the match the advisor just clicked.
  //
  // The param is `subtab`, not `sub`: the ticket says `?tab=compliance&sub=screening`, and
  // `ComplianceTab` reads `searchParams.get('subtab')` against `VALID_SUBTABS`. `sub` would
  // have been written and silently discarded, landing on the default `forms` sub-tab — the
  // same class of defect as TASK-829's `?tab=` on the wrong route.
  screening_potential_match: (g) => clientPath(g, 'compliance', '&subtab=screening'),
  // TASK-923: the three rows that gained a policy and had no destination. A row that offers
  // `investigate` and cannot say where is a button that does nothing — which is exactly what
  // the 2026-09-06 walkthrough recorded against the blocked upload.
  client_portal_upload_blocked: (g) => clientPath(g, 'documents'),
  client_portal_upload: (g) => clientPath(g, 'documents'),
  client_email_inbound: (g) => clientPath(g, 'timeline'),
};

// TASK-829 AC5: the TAB decides the route. `cio` and `personalization` live on
// `/portfolio/client/:clientId`; sending them to `/clients/:clientId` wrote a param that page
// discards in silence, landing the advisor on Timeline. `clientEvidenceHref` is the same
// resolver `SignalDossier` uses, so the two surfaces cannot disagree about where a signal's
// evidence lives.
const clientPath = (g: AlertGroup, tab: string, extraParams = ''): string => {
  const clientId = g.clients[0]?.clientId;
  return clientId ? clientEvidenceHref(clientId, tab, extraParams) : '/clients';
};

/** Human label for a disposition. The vocabulary is the server's; this is only its casing. */
const DISPOSITION_LABEL: Record<string, string> = {
  investigate: 'Investigate',
  assign: 'Assign',
  snooze: 'Snooze',
  resolve: 'Resolve',
  propose_trade: 'Propose trade',
  escalate: 'Escalate',
  read: 'Mark read',
};


/**
 * TASK-832: where a drift breach opens — the tab that holds the drift view, focused on the
 * sub-asset class the alert named.
 *
 * The SAC is on the alert already: `portfolio_drift_breach` emits it in `event_metadata`
 * (`portfolio-service/main.py:1216-1223`) and `groupAlerts` carries it through as the
 * position label. Without it the advisor lands on a full allocation and hunts for the row.
 */
const driftHref = (g: AlertGroup): string => {
  const clientId = g.clients[0]?.clientId;
  if (!clientId) return '/portfolios';

  // The SAC is on the alert itself: `portfolio_drift_breach` sets `related_entity_id` to the
  // sub-asset class and repeats it in `event_metadata`. `positionLabelOf` returns null here —
  // it looks for an instrument, and this signal is about an allocation slot.
  const sacs = new Set(
    g.alerts
      .map((a) => (typeof a.metadata?.sub_asset_class === 'string'
        ? a.metadata.sub_asset_class
        : a.relatedEntityId) || null)
      .filter((s): s is string => Boolean(s)),
  );

  // ONE breach → focus it. Several → open the tab unfocused rather than picking one, which
  // would be TASK-817's arbitrary `clients[0]` winner in a new place: the advisor would be
  // shown a specific row as though the card had been about it.
  const focus = sacs.size === 1 ? `&focusSac=${encodeURIComponent([...sacs][0])}` : '';
  return clientEvidenceHref(clientId, 'personalization', focus);
};

const chf = (v: number) =>
  `CHF ${Math.round(v).toLocaleString('de-CH').replace(/,/g, "'")}`;

const VISIBLE_CLIENTS = 3;

interface AlertGroupCardProps {
  group: AlertGroup;
  onDismissGroup?: (memberIds: string[]) => void;
  compact?: boolean;
  /**
   * TASK-819: the per-alert handlers. Aggregating by cause used to STRIP these — on the
   * default view no alert had a snooze, a deadline or its `quickActions`, and the per-alert
   * card was reachable only at group size one, i.e. only by filtering. Passing them lets the
   * card expand to real `AlertRow`s instead of being a dead end.
   */
  onDismissAlert?: (id: string) => void;
  onSnoozeAlert?: (id: string, until: string) => void;
  onMakeTaskFromAlert?: (alert: DashboardAlert) => void;
  /**
   * TASK-819: "open this alert" — mark read, and open the transaction-review modal where the
   * alert warrants one. Passed straight through to `AlertRow`; the page used to hang this off
   * the per-alert card it routed one-member groups to.
   */
  onSelectAlert?: (alert: DashboardAlert) => void;
  /**
   * TASK-820: carry out a disposition the server allows. Absent → those buttons still render
   * (the advisor can see what the policy permits) but only `investigate`, which navigates,
   * does anything. A button that silently does nothing is worse than one that is absent, so
   * this is the prop a caller must supply to make the rest live.
   */
  /**
   * TASK-923: `arg` carries the disposition's parameter — today only `snooze`'s expiry.
   * Optional so a host that cannot service a disposition simply omits the handler, and the
   * card renders the control DISABLED with a reason instead of an enabled silent no-op.
   */
  onDisposition?: (memberIds: string[], disposition: string, arg?: string) => void;
  /**
   * TASK-923 review: WHICH dispositions the host can actually complete.
   *
   * `Boolean(onDisposition)` was not enough, and the test that "proved" it passed only
   * because it rendered with no handler at all — a state the real page never reaches.
   * `AlertsPage` passes ONE handler for every disposition, so `propose_trade` stayed enabled,
   * kept its `· suggested` badge, and merely toasted. The live drift group still advertised
   * a trade proposal it could not open. A host must now name what it services.
   *
   * Omitted = every disposition (the pre-TASK-923 contract, for hosts that pass no handler
   * and therefore render everything disabled anyway).
   */
  servicedDispositions?: readonly string[];
}

export const AlertGroupCard: React.FC<AlertGroupCardProps> = ({
  group,
  onDismissGroup,
  compact = false,
  onDismissAlert,
  onSnoozeAlert,
  onMakeTaskFromAlert,
  onSelectAlert,
  onDisposition,
  servicedDispositions,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // TASK-819: a one-member group shows its signal without a click; larger groups start
  // collapsed. Size one USED TO render a full per-alert card via `AlertList`, and hiding that
  // behind a click would have been a fresh regression in the name of fixing one.
  //
  // Review finding 2: `useState(...)` alone runs ONCE. `group.key` for a normal cause is
  // `${category}:${causeKey}` and does not change when a filter removes members, so React
  // reuses this instance — a two-member group filtered to High stayed collapsed while an
  // unfiltered one-member group rendered expanded. That is the "page changes shape under a
  // filter" behaviour this ticket exists to remove, reintroduced one layer down. Deriving it
  // from the CURRENT size, with the advisor's own toggle taking precedence, fixes it.
  const [openOverride, setOpenOverride] = useState<boolean | null>(null);
  const signalsOpen = openOverride ?? group.alerts.length === 1;
  const setSignalsOpen = (next: boolean) => setOpenOverride(next);
  // `AlertRow` requires dismiss and snooze (only `onMakeTask` is optional there), and a row
  // whose controls are no-ops is worse than no row. So the section renders only when the
  // caller can genuinely service it — a caller that cannot keeps today's behaviour exactly.
  const perAlertAvailable = Boolean(onDismissAlert && onSnoozeAlert);
  // TASK-819: the badge states the DISTRIBUTION, not the max. `LEVEL_ORDER` fixes the reading
  // order so "1 high · 1 medium" never renders as "1 medium · 1 high" between two renders.
  const levelBreakdown = LEVEL_ORDER
    .map((lvl) => ({ lvl, n: group.levelCounts?.[lvl] ?? 0 }))
    .filter((e) => e.n > 0);
  const mixedLevels = levelBreakdown.length > 1;
  // TASK-820: the client link goes where this cause's evidence is — the same map the primary
  // action uses, so the two can never point at different surfaces for one signal.
  const destination = DESTINATIONS[group.causeKey];
  const clientHref = (clientId: string) =>
    destination ? destination(group) : `/clients/${clientId}`;
  const styles = LEVEL_STYLES[group.level] ?? LEVEL_STYLES.info;
  // TASK-820: the actions the SERVER allows, in a stable order, with the server's proposal
  // marked. No local rule table decides what may be offered.
  // TASK-923: role-gated, through the one shared helper. `allowed_roles` was in this
  // payload and read by nobody, so a compliance group offered `escalate`/`resolve` to an
  // advisor and the server refused.
  const role = useViewerRole();
  const allowed = (group.actionPolicy?.allowed_dispositions ?? []).filter((d) =>
    policyPermits(group.actionPolicy, d, role),
  );
  const recommended = group.actionPolicy?.recommended_disposition ?? null;
  // `dismiss` keeps its own dedicated control below (it carries a consequence warning), and
  // `read` is bookkeeping rather than a judgement, so neither joins the button row.
  const offered = allowed.filter((d) => d !== 'dismiss' && d !== 'read');
  // Why a disposition is deliberately absent — the owner's words, from the catalogue.
  const absentNotes = group.actionPolicy?.absent_disposition_notes ?? [];
  const shown = expanded ? group.clients : group.clients.slice(0, VISIBLE_CLIENTS);
  const hidden = group.clients.length - VISIBLE_CLIENTS;

  return (
    <div
      data-testid="alert-group-card"
      className={`rounded-lg border ${styles.border} bg-white p-4 shadow-sm`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 text-orange-500" aria-hidden />
          <h4 className="min-w-0 text-sm font-semibold text-gray-900">
            {group.causeLabelKey ? t(group.causeLabelKey, group.causeLabel) : group.causeLabel}
            <span className="ml-2 font-normal text-gray-500">
              {t('alerts.signals', {
                count: group.alerts.length,
                defaultValue: `${group.alerts.length} signals`,
              })} ·{' '}
              {t('alerts.clients', {
                count: group.clients.length,
                defaultValue: `${group.clients.length} clients`,
              })}
              {group.totalChf !== null &&
                ` · ${t('alerts.exposed', {
                  amount: chf(group.totalChf),
                  defaultValue: `${chf(group.totalChf)} exposed`,
                })}`}
            </span>
          </h4>
        </div>
        {/*
          `level` is a server enum (critical|high|medium|info) rendered verbatim, in the same
          class as an ISIN or a client name — data, not UI copy. Deliberately not keyed, so
          the badge always names the value the API actually returned.
        */}
        {/*
          TASK-821: the OBLIGATION band, beside — never instead of — the severity badge.

          The same re-profile signal was badged MANDATORY in red on the Radar and MEDIUM here,
          opposite ends of what looks like one urgency scale. Neither surface was wrong: they
          were showing two orthogonal facts and neither said which. `reprofile_due` is
          `level="medium"` (the emitter's severity) and `mandatory=True` (the policy
          catalogue's obligation) at the same time — a low-severity signal the firm has no
          discretion to ignore, which is exactly the distinction a suitability or KYC lapse
          turns on.

          Both fields already travel: `ActionPolicy.to_dict` serialises them
          (`signal_action_policy.py:92-93`) and `types/dashboard.ts:33-34` already declares
          them. There was no backend work here — only a surface that read one and not the other.
        */}
        {group.actionPolicy?.mandatory && (
          <span
            data-testid="alert-group-mandatory"
            title="An obligation the firm has no discretion to ignore. Separate from severity — a mandatory signal can be low severity."
            className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-800"
          >
            {t('alerts.mandatory', 'Mandatory')}
            {group.actionPolicy.sla_class && (
              <span data-testid="alert-group-sla-class" className="font-normal">
                {' · '}{group.actionPolicy.sla_class.replace(/_/g, ' ')}
              </span>
            )}
          </span>
        )}
        {mixedLevels ? (
          // Deliberately NOT keyed for translation, for the same reason the single badge is
          // not: `level` is a server enum rendered verbatim — data, not UI copy.
          <span
            data-testid="alert-group-level-distribution"
            className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-gray-700"
          >
            {levelBreakdown.map((e) => `${e.n} ${e.lvl}`).join(' · ')}
          </span>
        ) : (
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${styles.badge}`}>
            {group.level}
          </span>
        )}
      </div>

      {group.sentence && (
        <p className="mt-1 text-sm text-gray-600">
          {group.sentenceKey ? t(group.sentenceKey, group.sentence) : group.sentence}
        </p>
      )}

      {!compact && group.clients.length > 0 && (
        <ul className="mt-2 space-y-1">
          {shown.map((c) => (
            <li key={c.clientId ?? c.clientName} className="flex items-baseline gap-2 text-sm">
              {/*
                TASK-817: a SCOPE LABEL is not a destination. "Book-wide" and "No client
                scope" describe the absence of a client, and rendering them as buttons
                invited a click that navigated to whichever client id happened to be on
                the entry. An entry with no `clientId` is plain text.
              */}
              {c.clientId ? (
                <button
                  type="button"
                  className="shrink-0 font-medium text-gray-900 hover:text-blue-700 hover:underline"
                  onClick={() => navigate(clientHref(c.clientId!))}
                >
                  {c.clientNameKey ? t(c.clientNameKey, c.clientName) : c.clientName}
                </button>
              ) : (
                <span className="shrink-0 font-medium text-gray-900">
                  {c.clientNameKey ? t(c.clientNameKey, c.clientName) : c.clientName}
                </span>
              )}
              {c.positions.length > 0 && (
                <span className="min-w-0 truncate text-gray-500">{c.positions.join(', ')}</span>
              )}
              {c.chf > 0 && <span className="ml-auto shrink-0 text-gray-600">{chf(c.chf)}</span>}
            </li>
          ))}
          {hidden > 0 && !expanded && (
            <li>
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <ChevronDown className="h-3 w-3" />{' '}
                {t('alerts.showMore', { count: hidden, defaultValue: `+ ${hidden} more clients` })}
              </button>
            </li>
          )}
          {expanded && hidden > 0 && (
            <li>
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <ChevronUp className="h-3 w-3" /> {t('alerts.showFewer', 'Show fewer')}
              </button>
            </li>
          )}
        </ul>
      )}

      {/*
        TASK-819: the members, each as a real `AlertRow` with its own snooze, deadline and
        quickActions. Aggregating by cause used to strip all three, and the per-alert card was
        reachable ONLY at group size one — i.e. only by filtering, which is why the page
        appeared to change shape when a filter was applied. Nothing here is new behaviour:
        `AlertRow` already reads `alert.deadline` and already gates on `actionPolicy`. It was
        simply never mounted from a group.
      */}
      {!compact && perAlertAvailable && (
        <div className="mt-3 border-t border-gray-100 pt-2">
          <button
            type="button"
            data-testid="alert-group-signals-toggle"
            onClick={() => setSignalsOpen(!signalsOpen)}
            aria-expanded={signalsOpen}
            className="flex w-full items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
          >
            {signalsOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {signalsOpen
              ? t('alerts.hideSignals', 'Hide individual signals')
              : t('alerts.showSignals', {
                  count: group.alerts.length,
                  defaultValue: `Show ${group.alerts.length} individual signals`,
                })}
          </button>
          {signalsOpen && (
            <div data-testid="alert-group-signals" className="mt-2 space-y-2">
              {group.alerts.map((a) => (
                <AlertRow
                  key={a.id}
                  alert={a}
                  onDismiss={onDismissAlert!}
                  onSnooze={onSnoozeAlert!}
                  onMakeTask={onMakeTaskFromAlert}
                  onSelect={onSelectAlert}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {/*
          TASK-820: one button per disposition the SERVER allows, with the server's proposal
          marked. `investigate` carries the navigation, because "look at this" is what opening
          the evidence means; the rest report through the disposition path.
        */}
        {offered.map((d) => {
          const isRecommended = d === recommended;
          const label = t(`alerts.disposition.${d}`, DISPOSITION_LABEL[d] ?? d);
            // TASK-923. The 2026-09-06 walkthrough clicked group Assign, group Snooze and
            // "Propose trade · suggested" and recorded "no visible dialog, navigation or
            // feedback" for all three. The cause is here: `onDisposition?.()` — the page
            // passed no handler, optional chaining swallowed it, and the button was a
            // decoration. `investigate` worked only where `DESTINATIONS` had an entry.
            //
            // A control that cannot act is now DISABLED and says why. An advisor who clicks
            // a live-looking button and gets nothing learns the product is broken; one who
            // sees a disabled control with a reason learns where to go.
            const navigates = d === 'investigate' && Boolean(destination);
            const serviced = Boolean(onDisposition)
              && (!servicedDispositions || servicedDispositions.includes(d));
            const canAct = navigates || serviced;
            return (
            <div key={d} className="relative">
            <button
              type="button"
              data-testid={`alert-group-action-${d}`}
              data-recommended={isRecommended || undefined}
              disabled={!canAct}
              title={
                canAct
                  ? undefined
                  : d === 'investigate'
                    ? t('alerts.noDestination',
                        'No evidence page for this signal type yet — open the client record.')
                    : t('alerts.noGroupAction',
                        'Not available on a group here. Expand the group and act on a signal.')
              }
              onClick={() => {
                if (d !== 'snooze') setSnoozeOpen(false);
                if (navigates) { navigate(destination!(group)); return; }
                // Snooze is the one disposition that takes a parameter, and picking a
                // duration for the advisor would be choosing for them.
                if (d === 'snooze') { setSnoozeOpen((v) => !v); return; }
                onDisposition?.(group.memberIds, d);
              }}
              className={
                !canAct
                  ? 'cursor-not-allowed rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-400'
                  : isRecommended
                    ? 'rounded-md bg-[#002B45] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#003a5d]'
                    : 'rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50'
              }
            >
              {label}
              {isRecommended && canAct && (
                // TASK-784 (DEC-3): the system PROPOSES; the advisor still chooses. Marked,
                // never pre-selected and never the only button.
                <span data-testid="alert-group-recommended" className="ml-1 font-normal opacity-80">
                  · suggested
                </span>
              )}
            </button>
            {d === 'snooze' && snoozeOpen && onDisposition && (
              <>
              {/* TASK-923 review: a menu you can only leave by choosing is a trap. Escape and
                  an outside click both close it, and neither snoozes anything — cancelling is
                  a real answer to "how long", and the follow-up criterion asks for it. */}
              <div
                className="fixed inset-0 z-10"
                aria-hidden
                onClick={() => setSnoozeOpen(false)}
              />
              <div
                role="menu"
                data-testid="alert-group-snooze-menu"
                onKeyDown={(e) => { if (e.key === 'Escape') setSnoozeOpen(false); }}
                className="absolute left-0 top-full z-20 mt-1 min-w-[104px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
              >
                {SNOOZE_OPTIONS.map((o) => (
                  <button
                    key={o.days}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setSnoozeOpen(false);
                      onDisposition(group.memberIds, 'snooze', addDays(o.days));
                    }}
                    className="block w-full rounded px-2 py-1 text-left text-[11px] text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              </>
            )}
            </div>
          );
        })}
        {/*
          TASK-732: dismissal renders only when the server policy allows it — a mandatory
          compliance cause has no dismiss and the group card must not offer one. The group
          is keyed on (category, cause), so one policy governs every member.
        */}
        {onDismissGroup && policyPermits(group.actionPolicy, 'dismiss', role) && (
          <button
            type="button"
            onClick={() => onDismissGroup(group.memberIds)}
            // TASK-653: the consequence of dismissal is explicit, and the button
            // stays visually subordinate to the investigation action.
            title={t('alerts.dismissTitle', {
              count: group.alerts.length,
              defaultValue:
                `Removes these ${group.alerts.length} signal${group.alerts.length === 1 ? '' : 's'} ` +
                'from every attention surface without creating a task. The alerts stay in ' +
                'history and nothing is remediated.',
            })}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
          >
            <X className="h-3 w-3" /> {t('alerts.dismissGroup', 'Dismiss group')}
          </button>
        )}
      </div>

      {/*
        TASK-820: where a disposition is deliberately absent, SAY SO — in the owner's words,
        from the catalogue. Portfolio signals have no Resolve while compliance signals do, and
        with nothing said that reads as an inconsistency rather than a decision.
        Nothing authored yet → nothing rendered. An invented sentence about what the machine
        may conclude would be worse than the silence it replaces.
      */}
      {absentNotes.map((n) => (
        <p
          key={n.disposition}
          data-testid={`alert-group-absent-${n.disposition}`}
          className="mt-2 border-t border-gray-100 pt-2 text-[11px] text-gray-500"
        >
          {t(n.key, n.text)}
        </p>
      ))}
    </div>
  );
};

export default AlertGroupCard;
